/**
 * Smart Label Positioning for Edge Branch Labels
 * 
 * Calculates optimal label positions that:
 * 1. Are placed at the visual midpoint of the actual SVG path
 * 2. Stay within 10px of the path line (measured from label edge to path)
 * 3. Don't overlap with any nodes or other elements
 * 4. Fall back to alternative positions if the ideal spot is blocked
 */

// Label dimensions (should match the rect in Edge.js)
const LABEL_WIDTH = 40;
const LABEL_HEIGHT = 16;
const LABEL_PADDING = 6; // Extra padding around label for collision detection
const MAX_OFFSET_FROM_PATH = 10; // Maximum distance from the path line (label edge to path)

/**
 * Parse an SVG path string and extract all points/segments
 * Handles M (move), L (line), and Q (quadratic curve) commands
 */
function parsePathCommands(pathD) {
  if (!pathD) return [];
  
  const commands = [];
  // Match SVG path commands: letter followed by numbers (with optional decimals and negatives)
  const regex = /([MLQ])\s*([-\d.]+(?:\s+[-\d.]+)*)/gi;
  let match;
  
  while ((match = regex.exec(pathD)) !== null) {
    const cmd = match[1].toUpperCase();
    const nums = match[2].trim().split(/\s+/).map(Number);
    
    if (cmd === 'M' || cmd === 'L') {
      commands.push({ type: cmd, x: nums[0], y: nums[1] });
    } else if (cmd === 'Q') {
      // Q has control point (cx, cy) and end point (x, y)
      commands.push({ type: 'Q', cx: nums[0], cy: nums[1], x: nums[2], y: nums[3] });
    }
  }
  
  return commands;
}

/**
 * Calculate point on a quadratic bezier curve at parameter t (0-1)
 */
function quadraticBezierPoint(p0, p1, p2, t) {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

/**
 * Calculate the length of a line segment
 */
function lineLength(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Approximate the length of a quadratic bezier curve
 * Uses subdivision for reasonable accuracy
 */
function quadraticBezierLength(p0, p1, p2, segments = 10) {
  let length = 0;
  let prevPoint = p0;
  
  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const point = quadraticBezierPoint(p0, p1, p2, t);
    length += lineLength(prevPoint.x, prevPoint.y, point.x, point.y);
    prevPoint = point;
  }
  
  return length;
}

/**
 * Sample points along the entire path at regular intervals
 * Returns array of { x, y, t } where t is the normalized position (0-1) along the path
 */
function samplePathPoints(pathD, numSamples = 50) {
  const commands = parsePathCommands(pathD);
  if (commands.length < 2) return [];
  
  // First, calculate total path length and segment lengths
  const segments = [];
  let totalLength = 0;
  let currentPos = { x: 0, y: 0 };
  
  for (const cmd of commands) {
    if (cmd.type === 'M') {
      currentPos = { x: cmd.x, y: cmd.y };
    } else if (cmd.type === 'L') {
      const len = lineLength(currentPos.x, currentPos.y, cmd.x, cmd.y);
      segments.push({
        type: 'L',
        start: { ...currentPos },
        end: { x: cmd.x, y: cmd.y },
        length: len,
        startLength: totalLength,
      });
      totalLength += len;
      currentPos = { x: cmd.x, y: cmd.y };
    } else if (cmd.type === 'Q') {
      const p0 = { ...currentPos };
      const p1 = { x: cmd.cx, y: cmd.cy };
      const p2 = { x: cmd.x, y: cmd.y };
      const len = quadraticBezierLength(p0, p1, p2);
      segments.push({
        type: 'Q',
        p0, p1, p2,
        length: len,
        startLength: totalLength,
      });
      totalLength += len;
      currentPos = { x: cmd.x, y: cmd.y };
    }
  }
  
  if (totalLength === 0) return [];
  
  // Now sample points at regular intervals
  const points = [];
  
  for (let i = 0; i <= numSamples; i++) {
    const targetLength = (i / numSamples) * totalLength;
    
    // Find which segment contains this point
    for (const seg of segments) {
      if (targetLength >= seg.startLength && targetLength <= seg.startLength + seg.length) {
        const segT = seg.length > 0 ? (targetLength - seg.startLength) / seg.length : 0;
        
        let point;
        if (seg.type === 'L') {
          point = {
            x: seg.start.x + segT * (seg.end.x - seg.start.x),
            y: seg.start.y + segT * (seg.end.y - seg.start.y),
          };
        } else if (seg.type === 'Q') {
          point = quadraticBezierPoint(seg.p0, seg.p1, seg.p2, segT);
        }
        
        if (point) {
          points.push({
            ...point,
            t: i / numSamples,
            length: targetLength,
          });
        }
        break;
      }
    }
  }
  
  return points;
}

/**
 * Get the point at the visual midpoint of the path
 */
function getPathMidpoint(pathD) {
  const points = samplePathPoints(pathD, 100);
  if (points.length === 0) return null;
  
  // Find the point closest to t=0.5
  const midIndex = Math.floor(points.length / 2);
  return points[midIndex] || points[0];
}

/**
 * Check if a rectangle overlaps with a node
 */
function rectOverlapsNode(rectX, rectY, rectW, rectH, node) {
  if (!node.pos || !node.dims) return false;
  
  const nodeLeft = node.pos.x;
  const nodeRight = node.pos.x + node.dims.width;
  const nodeTop = node.pos.y;
  const nodeBottom = node.pos.y + node.dims.height;
  
  const rectLeft = rectX;
  const rectRight = rectX + rectW;
  const rectTop = rectY;
  const rectBottom = rectY + rectH;
  
  // Check for overlap
  return !(rectRight < nodeLeft || rectLeft > nodeRight || 
           rectBottom < nodeTop || rectTop > nodeBottom);
}

/**
 * Check if a label position overlaps with any nodes
 */
function labelOverlapsNodes(labelX, labelY, allNodes) {
  // Label is centered at (labelX, labelY)
  const halfW = (LABEL_WIDTH + LABEL_PADDING) / 2;
  const halfH = (LABEL_HEIGHT + LABEL_PADDING) / 2;
  
  for (const node of allNodes) {
    if (rectOverlapsNode(labelX - halfW, labelY - halfH, LABEL_WIDTH + LABEL_PADDING, LABEL_HEIGHT + LABEL_PADDING, node)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Calculate the perpendicular direction at a point on the path
 * Returns normalized vector { dx, dy }
 */
function getPerpendicularAtPoint(pathD, targetT) {
  const points = samplePathPoints(pathD, 100);
  if (points.length < 3) return { dx: 0, dy: -1 }; // Default to up
  
  // Find the index closest to targetT
  const targetIndex = Math.round(targetT * (points.length - 1));
  
  // Get points before and after for tangent calculation
  const prevIndex = Math.max(0, targetIndex - 1);
  const nextIndex = Math.min(points.length - 1, targetIndex + 1);
  
  const prev = points[prevIndex];
  const next = points[nextIndex];
  
  // Tangent direction
  const tx = next.x - prev.x;
  const ty = next.y - prev.y;
  const tLen = Math.sqrt(tx * tx + ty * ty);
  
  if (tLen === 0) return { dx: 0, dy: -1 };
  
  // Perpendicular (rotate 90 degrees)
  // We want to prefer "up" or "left" for labels
  const perpX = -ty / tLen;
  const perpY = tx / tLen;
  
  return { dx: perpX, dy: perpY };
}

/**
 * Calculate the minimum distance from a label center to the path
 * This accounts for the label dimensions - we measure from label edge, not center
 */
function calculateLabelEdgeDistanceFromPath(labelX, labelY, pathPoint) {
  const dx = labelX - pathPoint.x;
  const dy = labelY - pathPoint.y;
  const centerDist = Math.sqrt(dx * dx + dy * dy);
  
  // Subtract half the label dimension in the direction of the offset
  // This gives us the distance from the label edge to the path
  const halfW = LABEL_WIDTH / 2;
  const halfH = LABEL_HEIGHT / 2;
  
  // Use the smaller dimension as the effective "radius" for edge distance
  const effectiveRadius = Math.min(halfW, halfH);
  return Math.max(0, centerDist - effectiveRadius);
}

/**
 * Find candidate positions around a point on the path
 * Tries positions at various offsets perpendicular to the path
 * Ensures the label edge stays within MAX_OFFSET_FROM_PATH of the line
 */
function findCandidatePositions(pathD, centerPoint, allNodes) {
  const candidates = [];
  const perp = getPerpendicularAtPoint(pathD, centerPoint.t);
  
  // The label center needs to be offset by at least half the label height
  // to not overlap the line, plus some margin
  const minOffset = LABEL_HEIGHT / 2 + 2;
  
  // Try offsets perpendicular to the path
  // Start with positions that place the label edge close to the path
  const offsets = [
    minOffset,      // Just above/beside the path
    -minOffset,     // Just below/beside the path (other direction)
    minOffset + 4,  // A bit further
    -minOffset - 4,
    minOffset + 8,
    -minOffset - 8,
  ];
  
  for (const offset of offsets) {
    // Offset perpendicular to the path
    const x = centerPoint.x + perp.dx * offset;
    const y = centerPoint.y + perp.dy * offset;
    
    // Calculate the actual distance from label edge to path
    const edgeDist = calculateLabelEdgeDistanceFromPath(x, y, centerPoint);
    
    // Only consider positions where label edge is within MAX_OFFSET_FROM_PATH of the line
    if (edgeDist <= MAX_OFFSET_FROM_PATH) {
      if (!labelOverlapsNodes(x, y, allNodes)) {
        candidates.push({
          x,
          y,
          distance: edgeDist,
        });
      }
    }
  }
  
  // Sort by distance from path (prefer closer positions)
  candidates.sort((a, b) => a.distance - b.distance);
  
  return candidates;
}

/**
 * Main function: Calculate the optimal label position for a branch edge
 * 
 * Strategy:
 * 1. Find the visual midpoint of the path
 * 2. Try positions perpendicular to the path at the midpoint
 * 3. If blocked, try alternative positions along the path (40%, 60%, etc.)
 * 4. Always ensure the label edge is within 10px of the path line
 * 
 * @param {string} pathD - The SVG path string
 * @param {Array} allNodes - Array of all nodes for collision detection
 * @returns {{ x: number, y: number } | null} - The optimal label position, or null if no valid position
 */
export function calculateBranchLabelPosition(pathD, allNodes = []) {
  if (!pathD) return null;
  
  const points = samplePathPoints(pathD, 100);
  if (points.length === 0) return null;
  
  // Get the midpoint of the path
  const midIndex = Math.floor(points.length / 2);
  const midpoint = points[midIndex];
  if (!midpoint) return null;
  
  // Try to find a valid position near the midpoint
  const candidates = findCandidatePositions(pathD, midpoint, allNodes);
  
  if (candidates.length > 0) {
    return { x: candidates[0].x, y: candidates[0].y };
  }
  
  // If no valid position at midpoint, try other points along the path
  // Try positions progressively further from the midpoint
  const alternativeTs = [0.45, 0.55, 0.4, 0.6, 0.35, 0.65, 0.3, 0.7];
  
  for (const t of alternativeTs) {
    const index = Math.round(t * (points.length - 1));
    const point = points[index];
    if (!point) continue;
    
    const altCandidates = findCandidatePositions(pathD, point, allNodes);
    if (altCandidates.length > 0) {
      return { x: altCandidates[0].x, y: altCandidates[0].y };
    }
  }
  
  // Last resort: return a position near the midpoint
  // Use the perpendicular direction to offset from the path
  const perp = getPerpendicularAtPoint(pathD, 0.5);
  const offset = LABEL_HEIGHT / 2 + 2;
  return { 
    x: midpoint.x + perp.dx * offset, 
    y: midpoint.y + perp.dy * offset 
  };
}

/**
 * Simplified version that just returns the path midpoint
 * (for cases where collision detection isn't needed)
 */
export function getPathMidpointPosition(pathD) {
  const midpoint = getPathMidpoint(pathD);
  if (!midpoint) return null;
  return { x: midpoint.x, y: midpoint.y - 12 }; // Offset slightly above
}
